import { useMemo, useState } from 'react';
import { useTasksStore } from '@/stores/tasksStore';
import { TaskUI } from '@flexiplanner/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { api } from '@/lib/api';
import { taskToUI, numberToPriority } from '@flexiplanner/types';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getEnergyColor, getPriorityColor, formatDuration } from '@/lib/utils';
import { Search, SortAsc, SortDesc } from 'lucide-react';

type SortField = 'title' | 'priority' | 'energy' | 'duration' | 'deadline';
type SortOrder = 'asc' | 'desc';

export function TableView() {
  const { tasks, updateTask, deleteTask } = useTasksStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterEnergy, setFilterEnergy] = useState<string>('all');
  const [editingTask, setEditingTask] = useState<TaskUI | null>(null);

  const handleSaveTask = async (updates: Partial<TaskUI>) => {
    if (!editingTask) return;

    try {
      const input = {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        custom_attributes: updates.customAttributes,
      };
      const response = await api.tasks.update(editingTask.id, input);
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI((response.data as any).task || response.data));
      } else {
        updateTask(editingTask.id, updates);
      }
    } catch (err) {
      console.error('Failed to save activity', err);
      updateTask(editingTask.id, updates);
    }
  };

  const handleDeleteTask = async () => {
    if (!editingTask) return;

    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (err) {
      console.error('Failed to delete activity', err);
    }
  };

  // Filter and sort activities
  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(task => {
      // Search filter
      const matchesSearch =
        (task.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (task.description || '').toLowerCase().includes(searchQuery.toLowerCase());

      // Priority filter
      const matchesPriority =
        filterPriority === 'all' || task.priority === parseInt(filterPriority);

      // Energy filter
      const matchesEnergy =
        filterEnergy === 'all' || (task.energyLevel && task.energyLevel === parseInt(filterEnergy));

      return matchesSearch && matchesPriority && matchesEnergy;
    });

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'priority':
          comparison = (a.priority || 5) - (b.priority || 5);
          break;
        case 'energy':
          comparison = (a.energyLevel || 0) - (b.energyLevel || 0);
          break;
        case 'duration':
          comparison = (a.estimatedDuration || 0) - (b.estimatedDuration || 0);
          break;
        case 'deadline':
          const aTime = a.deadline ? new Date(a.deadline).getTime() : Infinity;
          const bTime = b.deadline ? new Date(b.deadline).getTime() : Infinity;
          comparison = aTime - bTime;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [tasks, searchQuery, sortField, sortOrder, filterPriority, filterEnergy]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = sortOrder === 'asc' ? SortAsc : SortDesc;

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-4 border-b p-4">
        <h2 className="text-xl font-semibold">Activity Table</h2>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search activities..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="1">Priority 1</SelectItem>
              <SelectItem value="2">Priority 2</SelectItem>
              <SelectItem value="3">Priority 3</SelectItem>
              <SelectItem value="4">Priority 4</SelectItem>
              <SelectItem value="5">Priority 5</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterEnergy} onValueChange={setFilterEnergy}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Energy" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Energy</SelectItem>
              <SelectItem value="1">Energy 1</SelectItem>
              <SelectItem value="2">Energy 2</SelectItem>
              <SelectItem value="3">Energy 3</SelectItem>
              <SelectItem value="4">Energy 4</SelectItem>
              <SelectItem value="5">Energy 5</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-background sticky top-0 border-b">
            <tr className="text-left">
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('title')}
                  className="font-semibold"
                >
                  Title
                  {sortField === 'title' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('priority')}
                  className="font-semibold"
                >
                  Priority
                  {sortField === 'priority' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('energy')}
                  className="font-semibold"
                >
                  Energy
                  {sortField === 'energy' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('duration')}
                  className="font-semibold"
                >
                  Duration
                  {sortField === 'duration' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('deadline')}
                  className="font-semibold"
                >
                  Deadline
                  {sortField === 'deadline' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4 font-semibold">Attributes</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.map(task => (
              <tr
                key={task.id}
                className="hover:bg-accent/50 cursor-pointer border-b transition-colors"
                onClick={() => setEditingTask(task)}
              >
                <td className="p-4">
                  <div>
                    <div className="font-medium">{task.title}</div>
                    {task.description && (
                      <div className="text-muted-foreground line-clamp-1 text-sm">
                        {task.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <Badge
                    variant="outline"
                    style={{
                      borderColor: getPriorityColor(task.priority),
                      color: getPriorityColor(task.priority),
                    }}
                  >
                    P{task.priority}
                  </Badge>
                </td>
                <td className="p-4">
                  {task.energyLevel && (
                    <Badge
                      variant="outline"
                      style={{
                        borderColor: getEnergyColor(task.energyLevel),
                        color: getEnergyColor(task.energyLevel),
                      }}
                    >
                      E{task.energyLevel}
                    </Badge>
                  )}
                </td>
                <td className="p-4 text-sm">
                  {task.estimatedDuration ? formatDuration(task.estimatedDuration) : '-'}
                </td>
                <td className="p-4 text-sm">
                  {task.deadline ? new Date(task.deadline).toLocaleDateString() : '-'}
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {task.attributes?.map(attr => (
                      <Badge key={attr} variant="secondary" className="text-xs">
                        {attr}
                      </Badge>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTasks.length === 0 && (
          <div className="text-muted-foreground py-12 text-center">No activities found</div>
        )}
      </div>

      <TaskEditor
        task={editingTask}
        open={!!editingTask}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
      />
    </div>
  );
}
